import { useSelector } from "react-redux";
import { EventsState } from "../../../../../../../store/types";
import { ChangeEvent, useState } from 'react';
import { batch, useDispatch } from 'react-redux';
import { validate as validateUuid } from 'uuid';
import {
	DndContext,
	closestCenter,
	PointerSensor,
	useSensor,
	useSensors,
	DragEndEvent,
	DragMoveEvent,
} from '@dnd-kit/core';
import {
	SortableContext,
	useSortable,
	verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

import { v4 as uuidV4 } from 'uuid';

import { ModuleGroups, ModuleMap, PageModule, PageModuleGroupModules, PageModuleType, Session, TranslateString } from "../../../../../../../types/working-model";
import { updateSession, updateWorkingSession } from '../../../../../../../store/actions/admin/create-event/session';
import Icon, { COLORS, ICONS } from '../../../../../../general-ui/icon';
import WaitingIndicator from '../../../../../../general-ui/waiting-indicator/waiting-indicator';
import BasicCard from "../../../../../../general-ui/basic-card/basic-card";
import Switch from "../../../../../../general-ui/switch/switch";
import { TogglePageModuleVisibility } from "../../../../../../../connection/page-modules";
import { useTypedSelector } from "../../../../../../../store/reducers/use-typed-selector";
import CollapsingList from "../../../../../../general-ui/collapsing-list/collapsing-list";
import SelectInput from "../../../../../../general-ui/select/select";
import TextInput from "../../../../../../general-ui/text-input/text";
import '../../panel/session-modules-lists.scss';
import './edit-tabs-list.scss';
import CustomOptions from "../../../../session/navigation/custom-options/custom-options";
import ModalComponent from "../../../../../../general-ui/modal/modal";
import Checkbox from "../../../../../../general-ui/checkbox/checkbox";
import { updateTranslateKey } from "../../../../../../../utils/utils";
import { GetDefaultTranslateString } from "../../../../../../../store/utils/create-event";
import { useParams } from "react-router";
import { ParamsProps } from "../../../../../../live-event/live-event";

const iconNameList = [ICONS.INFO_OUTLINE, ICONS.BAR_GRAPH, ICONS.DOCUMENTS, ICONS.IMAGES, ICONS.SIGNAL];
const iconOptions = iconNameList.map(name => ({
	value: name,
	label: <Icon name={name} size={14} color={COLORS.WHITE} />
}));


interface INestedSortableModuleProps {
	moduleId: string;
	module: PageModule;
	id: string;
	groupUuid: string
}

const NestedSortableModule: React.FC<INestedSortableModuleProps> = ({ id, module, groupUuid }) => {
	const {
		CreateSessionReducer: {
			workingSession
		},
		AuthReducer: {
			token
		}
	} = useSelector((state: EventsState) => state);

	const dispatch = useDispatch();

	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
	} = useSortable({ id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition: 'none',
	};


	const handleToggleModuleSwitch = async (module: PageModule, on: boolean) => {
		if (!workingSession || !module.id) return;

		const updatedSession: Session = {
			...workingSession,
			modules: workingSession!.modules.map(sessionModule => {
				if (sessionModule.id !== module.id) return sessionModule;
				return {
					...sessionModule,
					is_on: on,
				};
			}),
		};
		// update redux and database
		dispatch(updateWorkingSession(updatedSession));
		await TogglePageModuleVisibility(token!, module.id, on);
	};

	const handleDelete = async () => {
		if (!workingSession || !module.id) return;

		const updatedSession: Session = {
			...workingSession,
			module_grouping: workingSession?.module_grouping?.map(_moduleGroup => {
				if (groupUuid !== _moduleGroup.uuid) return _moduleGroup;

				return {
					..._moduleGroup,
					modules: _moduleGroup.modules.filter(_id => _id !== module.id)
				};
			})
		};
		batch(() => {
			// update db
			dispatch(updateSession(updatedSession, token!));
			// update redux
			dispatch(updateWorkingSession(updatedSession));
		});
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
		>
			<div className="nested-module-card-container">
				<BasicCard className="nested-module-card">
					<div className="title">
						<div className="drag-handle" {...attributes} {...listeners}>
							<Icon name={ICONS.DRAG_HANDLE} size={12} color={COLORS.GRAY} />
						</div>
						{ModuleMap[module.type]
							//keeping for now just in case we want the custom translated module names
							// module?.content?.custom_heading?.base?.trim()?.length
							// 	? module.content.custom_heading.base
							// 	: ModuleMap[module.type]
						}
					</div>
					<div>
						<Switch
							value={module.id}
							onClick={(_, on) => handleToggleModuleSwitch(module, on)}
							on={module.is_on}
						/>
					</div>
					<button className="delete no-style" onClick={handleDelete}>
						<Icon name={ICONS.DELETE} size={12} color={COLORS.GRAY} />
					</button>
				</BasicCard>
			</div>
		</div>
	);
};

interface ISortableTopLevelSessionModuleProps {
	moduleGroup: PageModuleGroupModules;
	workingSession: Session;
	id: string;
	shouldCloseAllLists: boolean;
}
const SortableTopLevelSessionModule: React.FC<ISortableTopLevelSessionModuleProps> = ({
	id,
	moduleGroup,
	workingSession,
	shouldCloseAllLists,
}) => {
	const token = useTypedSelector(state => state.AuthReducer.token);
	const [addModuleOpen, setAddModuleOpen] = useState(false);
	const [blankModalOpen, setBlankModalOpen] = useState(false);
	const [isSelectModuleOpen, setIsSelectModuleOpen] = useState(false);
	const [selectedModules, setSelectedModules] = useState(moduleGroup.modules);

	const { language } = useParams() as ParamsProps;

	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
	} = useSortable({ id });
	const dispatch = useDispatch();

	const style = {
		transform: CSS.Transform.toString(transform),
		transition: 'none',
	};

	const handleToggleTopLevelSwitch = async (
		selectedModuleGroup: PageModuleGroupModules,
		on: boolean
	) => {
		const updatedSession: Session = {
			...workingSession,
			module_grouping: workingSession?.module_grouping?.map(moduleGroup => {
				if (selectedModuleGroup.uuid !== moduleGroup.uuid) return moduleGroup;
				return {
					...moduleGroup,
					is_on: on,
				};
			}),
		};

		batch(() => {
			// update db
			dispatch(updateSession(updatedSession, token!));
			// update redux
			dispatch(updateWorkingSession(updatedSession));
		});
	};

	const handleIconChange = async (icon: string) => {
		const updatedSession: Session = {
			...workingSession,
			module_grouping: workingSession?.module_grouping?.map(_moduleGroup => {
				if (moduleGroup.uuid !== _moduleGroup.uuid) return _moduleGroup;
				return {
					...moduleGroup,
					icon
				};
			}),
		};
		batch(() => {
			// update db
			dispatch(updateSession(updatedSession, token!));
			// update redux
			dispatch(updateWorkingSession(updatedSession));
		});
	};

	const handleNameChange = async ({ target }: ChangeEvent<HTMLInputElement>) => {
		const updatedSession: Session = {
			...workingSession,
			module_grouping: workingSession?.module_grouping?.map(_moduleGroup => {
				if (moduleGroup.uuid !== _moduleGroup.uuid) return _moduleGroup;

				// This was previously static text, leaving a saftey check for backwards compatibility
				const translateString = typeof moduleGroup.name === "string"
					? GetDefaultTranslateString(moduleGroup.name)
					: moduleGroup.name;

				return {
					...moduleGroup,
					name: updateTranslateKey({
						translateString,
						input: target.value,
						baseLanguage: workingSession.default_language,
						language
					})
				};
			}),
		};
		batch(() => {
			// update db
			dispatch(updateSession(updatedSession, token!));
			// update redux
			dispatch(updateWorkingSession(updatedSession));
		});
	};
	const handleSelectedModules = async () => {
		const updatedSession: Session = {
			...workingSession,
			module_grouping: workingSession?.module_grouping?.map(_moduleGroup => {
				if (moduleGroup.uuid !== _moduleGroup.uuid) return _moduleGroup;
				return {
					...moduleGroup,
					modules: selectedModules
				};
			}),
		};
		batch(() => {
			// update db
			dispatch(updateSession(updatedSession, token!));
			// update redux
			dispatch(updateWorkingSession(updatedSession));
		});
		setIsSelectModuleOpen(false);
	};

	const handleDelete = async () => {
		const updatedSession: Session = {
			...workingSession,
			module_grouping: workingSession?.module_grouping?.filter(_moduleGroup => {
				return (moduleGroup.uuid !== _moduleGroup.uuid);
			})
		};
		batch(() => {
			// update db
			dispatch(updateSession(updatedSession, token!));
			// update redux
			dispatch(updateWorkingSession(updatedSession));
		});
	};


	function addModule() {
		setAddModuleOpen(true);
		setIsSelectModuleOpen(false);
	}

	function selectModule() {
		setAddModuleOpen(false);
		setIsSelectModuleOpen(true);
	}
	function closeSelectModule() {
		setIsSelectModuleOpen(false);
		setSelectedModules(moduleGroup.modules);
	}

	function handleCheckedModule(id: number | string, isOn: boolean) {
		if (isOn) {
			setSelectedModules(prev => [...prev, Number(id)]);
		} else {
			setSelectedModules(prev => prev.filter(_id => _id !== Number(id)));
		}
	}

	let name: string;
	// This was previously static text, leaving a saftey check for backwards compatibility
	//Check to see if "name" is a Translate string, if so return the translated value, if not return the static text
	const stringName = moduleGroup?.name as ModuleGroups;
	const translateName = moduleGroup?.name as TranslateString;
	if (typeof translateName === 'object') {
		// allow specific language translations to be overridden if they want unique content from the base language 
		name = translateName[language] as string ?? translateName.base;
	} else {
		name = stringName;
	}

	return (
		<div
			className="sortable-card-wrapper"
			ref={setNodeRef}
			style={style}
		>
			<CollapsingList
				label=""
				closeAll={shouldCloseAllLists}
				customHeader={(toggleOpen, isOpen) => (
					<BasicCard p={0}>
						<div className="top-level-card">
							<div className="drag-handle" {...attributes} {...listeners}>
								<Icon name={ICONS.DRAG_HANDLE} size={12} color={COLORS.GRAY} />
							</div>
							<button className="no-style small" onClick={() => toggleOpen()}>
								<Icon name={isOpen ? ICONS.KEYBOARD_ARROW_DOWN : ICONS.KEYBOARD_ARROW_RIGHT} color={COLORS.WHITE} size={16} />
							</button>
							<div className="top-level-card-content">
								<SelectInput
									noMaxHeight
									mainIcon={<Icon name={ICONS.KEYBOARD_ARROW_DOWN} size={14} color={COLORS.WHITE} />}
									options={iconOptions}
									selected={moduleGroup.icon || ""}
									onChange={handleIconChange}
								/>
								<TextInput
									value={name}
									onChange={handleNameChange}
									placeholder="Tab title"
								/>
								<div>
									<Switch
										value={name}
										onClick={(_, on) => handleToggleTopLevelSwitch(moduleGroup, on)}
										on={moduleGroup.is_on}
									/>
								</div>
							</div>
							<button className="delete no-style" onClick={handleDelete}>
								<Icon name={ICONS.DELETE} size={12} color={COLORS.GRAY} />
							</button>
						</div>
					</BasicCard>
				)}
			>
				<SortableContext
					items={moduleGroup.modules?.map((moduleId: number) => moduleId.toString())}
					strategy={verticalListSortingStrategy}
				>
					{moduleGroup.modules.map((moduleId: number) => {
						const module = workingSession.modules.find((module: PageModule) => module.id === moduleId);
						if (!module) return null;
						// FUTURE DEV - hiding quizzes
						if (module.type === PageModuleType.quizzes) return null;
						return (
							<NestedSortableModule
								groupUuid={moduleGroup.uuid}
								key={moduleId}
								id={moduleId.toString()}
								moduleId={moduleId.toString()}
								module={module}
							/>
						);
					})}
					<BasicCard p={0} className="custom-button-wrapper">
						<button className="custom-section-button" onClick={selectModule}>
							<Icon name={ICONS.ADD} color={COLORS.CYAN} size={12} /> <span>Add Section</span>
						</button>
					</BasicCard>
				</SortableContext>
			</CollapsingList>
			<ModalComponent
				cancellable
				closeable={false}
				title="Select Sections"
				open={isSelectModuleOpen}
				onRequestClose={closeSelectModule}
				footer={<>
					<button className="button" onClick={closeSelectModule}>Cancel</button>
					<button className="button primary" onClick={handleSelectedModules}>Add</button>
				</>}
			>
				{workingSession.modules.map(({ id, type, content_modules }) => {
					//slideshows and quizzes are not currently in designs, do not show as an option
					if (type === PageModuleType.slideshows || type === PageModuleType.quizzes) return null;
					if (!id) return null;
					return (
						<BasicCard p={12} key={id} className="module-checkbox-container">
							<Checkbox
								// activeColor="cyan"
								value={id}
								checked={selectedModules.includes(id)}
								onChange={handleCheckedModule}
								label={<>
									{ModuleMap[type]}
									{content_modules
										? <i>({content_modules.length})</i>
										: null
									}

								</>}
							/>
						</BasicCard>
					);
				})}
				<BasicCard p={0} className="custom-button-wrapper">
					<button className="custom-section-button" onClick={addModule}>
						<Icon name={ICONS.ADD} color={COLORS.CYAN} size={12} /> <span>Custom Section</span>
					</button>
				</BasicCard>

			</ModalComponent>
			<CustomOptions
				groupUuid={moduleGroup.uuid}
				addModuleOpen={addModuleOpen}
				blankModalOpen={blankModalOpen}
				setAddModuleOpen={setAddModuleOpen}
				setBlankModalOpen={setBlankModalOpen}
			/>
		</div>

	);
};

const EditTabsList: React.FC = () => {
	const {
		CreateSessionReducer: {
			workingSession
		},
		AuthReducer: {
			token
		}
	} = useSelector((state: EventsState) => state);
	const [shouldCloseAllLists, setShouldCloseAllLists] = useState(false);
	const dispatch = useDispatch();
	const sensors = useSensors(
		useSensor(PointerSensor, {
			// Require the pointer/mouse to move by 10 pixels before activating
			activationConstraint: {
				distance: 10
			},
		})
	);
	const handleTopLevelDragEnd = (event: DragEndEvent) => {
		if (!event.over) return;

		setShouldCloseAllLists(false);
		if (validateUuid(event.active.id)) {
			if (workingSession?.module_grouping) {
				const _modules = [...workingSession.module_grouping];
				const startIndex = _modules.findIndex(
					(module: PageModuleGroupModules) => module.uuid === event.active.id
				);
				const newIndex = _modules.findIndex(
					(module: PageModuleGroupModules) => module.uuid === event?.over?.id
				);

				if (startIndex !== newIndex) {
					const [module] = _modules.splice(startIndex, 1);
					_modules.splice(newIndex, 0, module);
					const updatedSession: Session = {
						...workingSession,
						module_grouping: _modules,
					};
					batch(() => {
						// update db
						dispatch(updateSession(updatedSession, token!));
						// update redux
						dispatch(updateWorkingSession(updatedSession));
					});
				}
			}

		} else if (workingSession?.module_grouping) {
			// else is nested sortable modules:
			const activeId = Number(event.active.id);
			const overId = Number(event?.over?.id);
			// first find the module grouping that the activeId belongs to
			// then do the splicing
			// then update the workingSession locally
			// then save in db and redux
			let hasChangedPosition = true;
			const updatedSession: Session = {
				...workingSession,
				module_grouping: workingSession?.module_grouping.map((module: PageModuleGroupModules) => {
					const matched = module.modules.find((moduleId: number) => moduleId === Number(activeId));
					if (!matched) return module;
					const _modules = [...module.modules];
					const startIndex = _modules.findIndex((module: number) => module === activeId);
					const newIndex = _modules.findIndex((module: number) => module === overId);
					if (startIndex === newIndex) {
						hasChangedPosition = false;
						return module;
					}
					const [moduleSplice] = _modules.splice(startIndex, 1);
					_modules.splice(newIndex, 0, moduleSplice);
					return {
						...module,
						modules: _modules,
					};
				}),
			};

			if (hasChangedPosition) {
				batch(() => {
					// update db
					dispatch(updateSession(updatedSession, token!));
					// update redux
					dispatch(updateWorkingSession(updatedSession));
				});
			}
		}
	};

	// if top level card is moved, we should close all lists so it works properly
	const handleDragMove = (event: DragMoveEvent) => {
		if (validateUuid(event.active.id)) {
			if (!shouldCloseAllLists) {
				setShouldCloseAllLists(true);
			}
		}
	};

	const handleAddTab = () => {
		if (!workingSession) return;
		const newModuleGroup: PageModuleGroupModules = {
			name: GetDefaultTranslateString(),
			uuid: uuidV4(),
			is_on: true,
			modules: [],
			icon: ICONS.SIGNAL
		};
		const prevGroupings = workingSession.module_grouping ? workingSession.module_grouping : [];
		const updatedSession: Session = {
			...workingSession,
			module_grouping: [...prevGroupings, newModuleGroup],
		};
		batch(() => {
			// update db
			dispatch(updateSession(updatedSession, token!));
			// update redux
			dispatch(updateWorkingSession(updatedSession));
		});

	};

	if (!workingSession) return null;
	if (!workingSession?.module_grouping) return <WaitingIndicator />;
	return (
		<div className="edit-tabs-list session-module-grouping-list-container">
			<div className="small-tabs-header">
				<p>Tabs</p>
				<button className="no-style small" onClick={handleAddTab}>
					<Icon name={ICONS.ADD} color={COLORS.CYAN} size={12} />
					Add tab
				</button>
			</div>

			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragEnd={handleTopLevelDragEnd}
				modifiers={[restrictToParentElement]}
				onDragMove={handleDragMove}
				autoScroll={false}
			>
				<SortableContext
					items={workingSession?.module_grouping?.map((module: PageModuleGroupModules) => module.uuid)}
					strategy={verticalListSortingStrategy}
				>
					{workingSession.module_grouping.map((moduleGroup: PageModuleGroupModules) => {
						return (
							<SortableTopLevelSessionModule
								key={moduleGroup.uuid}
								id={moduleGroup.uuid}
								workingSession={workingSession}
								moduleGroup={moduleGroup}
								shouldCloseAllLists={shouldCloseAllLists}
							/>
						);
					})}
				</SortableContext>
			</DndContext>
		</div>
	);
};

export default EditTabsList;